import { Box, Stack } from '@mui/material'
import { FunctionComponent, SetStateAction, useEffect, useState } from 'react'
import { IPostReleaseNote } from '../../../shared/services/release-notes/release-notes-model'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment, { Moment } from 'moment';
import { FormTextInputDisplay } from '../form-field-components/new-and-edit/form-text-input';
import { FormDateInputDisplay } from '../form-field-components/new-and-edit/form-date-input';
import { FormSelectInputDisplay } from '../form-field-components/new-and-edit/form-select-input';
import { FormFileInputDisplay } from '../form-field-components/new-and-edit/form-file-input';
import { FormRadioInputDisplay } from '../form-field-components/new-and-edit/form-radio-input';
import { DialogSaveButtons } from '../miscellaneous-components/dialog-save-buttons';
import { useNavigate } from 'react-router-dom';
import { RELEASE_NOTE_TYPES } from '../../../shared/constants/release-note-types';

export const ReleaseNoteForm: FunctionComponent<{
  newReleaseNote: IPostReleaseNote & { uploadedResources?: any[] },
  submitHandler: React.Dispatch<React.SetStateAction<any>>,
  isEditPage?: boolean,
  editField?: string,
  setEditField?: React.Dispatch<React.SetStateAction<any>>,
  errorMessage?: string,
  errorCode?: number,
  setErrorMessage?: React.Dispatch<SetStateAction<string | undefined>>,
  setErrorCode?: React.Dispatch<SetStateAction<number | undefined>>,
  isSubmitting: boolean,
}> = ({
  newReleaseNote,
  submitHandler,
  isEditPage = false,
  editField,
  setEditField,
  errorMessage,
  errorCode,
  setErrorMessage,
  setErrorCode,
  isSubmitting,
}) => {
    const navigate = useNavigate()
    //State
    const [releaseNoteFileData, setReleaseNoteFileData] = useState<any[]>([]);
    const [removedResourceIds, setRemovedResourceIds] = useState<string[]>([]);
    //Formik
    const releaseNoteSchema = Yup.object().shape({
      title: Yup.string().required(`Title is required`),
      description: Yup.string().required(`Description is required`),
      releasedDate: Yup.string().required(`Release date is required`),
      status: Yup.string().required(`Status is required`),
      released: Yup.boolean().required(`Released status is required`),
    })

    const formik = useFormik<any>({
      initialValues: {
        title: newReleaseNote.title,
        description: newReleaseNote.description,
        releasedDate: newReleaseNote.releasedDate,
        status: newReleaseNote.status,
        released: newReleaseNote.released,
      },
      validationSchema: releaseNoteSchema,
      onSubmit: (values) => {
        submitHandler({ values, files: releaseNoteFileData, removedResourceIds })
      }
    })

    //Use Effects
    useEffect(() => {
      if (!isEditPage) {
        formik.setValues({
          title: newReleaseNote.title,
          description: newReleaseNote.description,
          releasedDate: newReleaseNote.releasedDate,
          status: newReleaseNote.status,
          released: newReleaseNote.released,
        })
      }
    }, [newReleaseNote, isEditPage])

    const cancelEditHandler = () => {
      if (setEditField) {
        setEditField('')
      }
      formik.setTouched({})
      formik.setErrors({})
      if (setErrorCode) {
        setErrorCode(undefined)
      }
      if (setErrorMessage) {
        setErrorMessage(undefined)
      }
      if (newReleaseNote) {
        formik.setValues({
          title: newReleaseNote.title,
          description: newReleaseNote.description,
          releasedDate: newReleaseNote.releasedDate,
          status: newReleaseNote.status,
          released: newReleaseNote.released,
        })
      }
      setReleaseNoteFileData([]);
      setRemovedResourceIds([]);
    }


    const removeReleaseNoteFileHandler = () => {
      setReleaseNoteFileData([]);
    }

    const dateHandler = (date: Moment, name: string) => {
      if (date) {
        formik.setValues({ ...formik.values, [name]: date.format('YYYY-MM-DD') })
      }
    }

    return (
      <Box component='form' onSubmit={formik.handleSubmit}>
        <FormTextInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          isTopRow={true}
          disabled={isEditPage && editField !== 'title'}
          title='Title'
          name='title'
          value={formik.values.title}
          touched={formik.touched.title}
          errors={formik.errors.title}
          cancelEditHandler={cancelEditHandler}
          onChange={formik.handleChange}
          customErrorMessage={errorCode === 1 ? errorMessage : undefined}
        />
        <FormTextInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'description'}
          title={`Description`}
          name='description'
          value={formik.values.description || ''}
          touched={formik.touched.description}
          errors={formik.errors.description}
          onChange={formik.handleChange}
          cancelEditHandler={cancelEditHandler}
          customErrorMessage={errorCode === 2 ? errorMessage : undefined}
          multiline={true}
          rows={10}
          helperText='Leave a blank line between paragraphs. Start a line with "- " for a bullet point.'
        />
        <FormDateInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'releasedDate'}
          title='Release Date'
          name='releasedDate'
          value={moment(formik.values.releasedDate)}
          touched={formik.touched.releasedDate}
          errors={formik.errors.releasedDate}
          onChange={(date: Moment) => dateHandler(date, 'releasedDate')}
          cancelEditHandler={cancelEditHandler}
          customErrorMessage={errorCode === 3 ? errorMessage : undefined}
        />
        <FormFileInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'releaseNoteFiles'}
          title='Release Note Files'
          name='releaseNoteFiles'
          value={undefined}
          onChange={() => { }}
          cancelEditHandler={cancelEditHandler}
          fileData={releaseNoteFileData}
          setFileData={setReleaseNoteFileData}
          onFileUploadSubmit={() => { }}
          onImageRemove={removeReleaseNoteFileHandler}
          allowedFileTypes={['PDF', 'XLS', 'XLSX', 'CSV', 'JPG', 'PNG', 'GIF', 'JPEG']}
          uploadButtonText='Upload File'
          uploadedResources={newReleaseNote.uploadedResources}
          removedResourceIds={removedResourceIds}
          onToggleResourceRemove={(id: string, removed: boolean) => {
            setRemovedResourceIds(prev => removed ? [...prev, id] : prev.filter(x => x !== id))
          }}
        />
        <FormSelectInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          cancelEditHandler={cancelEditHandler}
          disabled={isEditPage && editField !== 'status'}
          forceOutlined={isEditPage && editField === 'status'}
          title='Status'
          name='status'
          value={formik.values.status}
          onChange={formik.handleChange}
          array={RELEASE_NOTE_TYPES.map(type => type.value)}
        />
        <FormRadioInputDisplay
          isEditPage={isEditPage}
          editField={editField}
          setEditField={setEditField}
          disabled={isEditPage && editField !== 'released'}
          cancelEditHandler={cancelEditHandler}
          title='Released Status'
          name='released'
          value={formik.values.released}
          touched={formik.touched.released}
          errors={formik.errors.released}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value === 'true';
            formik.setFieldValue('released', value);
          }}
          optionOneLabel="Don't Release to Users"
          optionOneValue={false}
          optionTwoValue={true}
          optionTwoLabel="Release to Users"
          isBottomRow={true}
        />
        {!isEditPage &&
          <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
            <DialogSaveButtons
              isForm={true}
              isSubmitting={isSubmitting}
              cancelHandler={() => navigate(`/super-admin/release-notes`)}
              submitHandler={formik.submitForm}
            />
          </Stack>}
      </Box>
    )
  }